import { alpha, createTheme } from '@mui/material';
const palette = {
    primary: {
        main: '#175788',
        dark: '#093762',
        contrastText: '#FFF',
    },
    secondary: {
        main: '#30A8DF',
        contrastText: '#FFF',
        light: '#5DC0E7',
    },
    text: {
        primary: '#3D3D3D',
        secondary: '#175788',
        disabled: '#E7E7E7',
        hint: '#A3A3A3',
        link: '#38A9DD',
    },
    action: {
        hover: 'rgba(0,0,0, 0.04)',
    },
    info: {
        main: '#30A8DF',
        contrastText: '#FFF',
        light: '#DBF4FF',
    },
    error: {
        main: '#FF0B1E',
        contrastText: '#FFF',
        light: '#FFE5E7',
    },
    warning: {
        main: '#FFBA00',
        contrastText: '#FFF',
        light: '#FFF5D9',
    },
    success: {
        main: '#78BE21',
        contrastText: '#FFF',
        light: '#E4F2D2',
    },
    background: {
        default: '#F8F8F8',
        paper: '#FFFFFF',
    },
    midmark: {
        greyBlue: '#C1CDD8',
    },
};
export const MidmarkTheme = createTheme({
    components: {
        MuiFormLabel: {
            styleOverrides: {
                asterisk: {
                    color: palette.error.main,
                    fontWeight: 'normal',
                    fontSize: '0.75em',
                    display: 'inline-block',
                    transform: 'translateY(-2px)',
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                disableRipple: true,
                disableFocusRipple: true,
                disableTouchRipple: true,
            },
            styleOverrides: {
                root: {
                    color: palette.text.hint,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '4px',
                    backgroundColor: palette.text.hint,
                    color: palette.background.paper,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    marginRight: '10px',
                },
            },
        },
        MuiScopedCssBaseline: {
            styleOverrides: {
                root: {
                    fontSize: '1em',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-input, & .MuiSelect-select.MuiInputBase-input': {
                        minHeight: '1.1876em',
                        height: '1.1876em',
                        lineHeight: 1,
                    },
                    '& input::-webkit-input-placeholder': {
                        fontStyle: 'italic',
                        transform: 'scale(0.9)',
                        transformOrigin: 'left',
                    },
                    '& input::-moz-placeholder': {
                        fontStyle: 'italic',
                        transform: 'scale(0.9)',
                        transformOrigin: 'left',
                    },
                    '& textarea::-webkit-input-placeholder': {
                        fontStyle: 'italic',
                        transform: 'scale(0.9)',
                        transformOrigin: 'left',
                    },
                    '& textarea::-moz-placeholder': {
                        fontStyle: 'italic',
                        transform: 'scale(0.9)',
                        transformOrigin: 'left',
                    },
                    // This is a fix for an opacity bug on Safari. The fix was applied in React MUI v5. This is our manual fix
                    // until we can upgrade.
                    // https://github.com/mui-org/material-ui/pull/23375
                    '&$disabled': {
                        WebkitTextFillColor: palette.text.disabled,
                    },
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
                disableTouchRipple: true,
            },
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true,
                disableTouchRipple: true,
                disableFocusRipple: true,
            },
        },
        MuiDialog: {
            defaultProps: {
                container: () => document.getElementById('modal-container') || document.body,
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
                color: 'primary',
                disableRipple: true,
                disableFocusRipple: true,
                disableTouchRipple: true,
            },
            styleOverrides: {
                root: {
                    padding: `12px 20px`,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    zIndex: 0,
                    height: 40,
                    position: 'relative',
                },
                contained: {
                    boxShadow: 'none',
                },
                containedPrimary: {
                    boxShadow: 'none',
                    '&::before': {
                        content: '" "',
                        display: 'block',
                        position: 'absolute',
                        borderRadius: 'inherit',
                        width: '100%',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.25)',
                        transition: 'ease .15s',
                        clipPath: 'inset(0px 100% 0px 0px)',
                    },
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: palette.primary.main,
                    },
                    '&:hover:before': {
                        clipPath: 'inset(0px 0px 0px 0px)',
                    },
                },
                containedSecondary: {
                    color: palette.text.primary,
                    fontWeight: 'normal',
                    backgroundColor: palette.midmark.greyBlue,
                    '&::before': {
                        content: '" "',
                        display: 'block',
                        position: 'absolute',
                        borderRadius: 'inherit',
                        width: '100%',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        backgroundColor: alpha(palette.primary.main, 0.25),
                        transition: 'ease .15s',
                        clipPath: 'inset(0px 0px 0px 100%)',
                    },
                    '&:hover': {
                        backgroundColor: palette.midmark.greyBlue,
                        boxShadow: 'none',
                    },
                    '&:hover:before': {
                        clipPath: 'inset(0px 0px 0px 0px)',
                    },
                },
                containedError: {
                    boxShadow: 'none',
                    '&::before': {
                        content: '" "',
                        display: 'block',
                        position: 'absolute',
                        borderRadius: 'inherit',
                        width: '100%',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        background: '#FF414F',
                        transition: 'ease .15s',
                        clipPath: 'inset(0px 100% 0px 0px)',
                    },
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: palette.error.main,
                    },
                    '&:hover:before': {
                        clipPath: 'inset(0px 0px 0px 0px)',
                    },
                },
                outlined: {
                    padding: `12px 20px`,
                },
                outlinedPrimary: {
                    color: palette.text.primary,
                    borderColor: palette.text.hint,
                    boxShadow: 'none',
                    '&::before': {
                        content: '" "',
                        display: 'block',
                        position: 'absolute',
                        borderRadius: '5px',
                        width: '100%',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        background: palette.midmark.greyBlue,
                        transition: 'ease .15s',
                        clipPath: 'inset(0px 100% 0px 0px)',
                    },
                    '&:hover': {
                        boxShadow: 'none',
                        borderColor: palette.text.hint,
                    },
                    '&:hover:before': {
                        clipPath: 'inset(0px 0px 0px 0px)',
                    },
                },
                text: {
                    '&:hover': {
                        color: palette.primary.dark,
                        backgroundColor: 'transparent',
                    },
                },
                textPrimary: {
                    '&:hover': {
                        color: palette.primary.dark,
                        backgroundColor: 'transparent',
                    },
                },
                textSecondary: {
                    '&:hover': {
                        color: palette.secondary.light,
                        backgroundColor: 'transparent',
                    },
                },
                iconSizeSmall: {
                    '& > *:first-of-type': {
                        fontSize: 'inherit',
                    },
                },
                iconSizeMedium: {
                    '& > *:first-of-type': {
                        fontSize: 'inherit',
                    },
                },
                iconSizeLarge: {
                    '& > *:first-of-type': {
                        fontSize: 'inherit',
                    },
                },
                sizeSmall: {
                    padding: `12px 20px`,
                },
                sizeLarge: {
                    padding: `12px 20px`,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: '5px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected, &.Mui-selected:hover, &:hover, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible': {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px',
                        borderColor: 'rgba(0, 0, 0, 0.5)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '3px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '3px',
                    },
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'always',
            },
            styleOverrides: {
                root: {
                    color: palette.text.link,
                },
            },
        },
    },
    palette: palette,
    shape: {
        borderRadius: 6,
    },
    mixins: {
        toolbar: { minHeight: 50 },
    },
    typography: {
        fontFamily: 'Ubuntu',
        fontSize: 14,
        // The below rem calculations are based on a base font size of 16px
        h1: {
            fontWeight: 'bold',
            fontSize: '2.125rem',
            lineHeight: '2.44rem',
            letterSpacing: '0.17px',
            opacity: 1,
        },
        h2: {
            fontWeight: 'normal',
            fontSize: '1.5rem',
            lineHeight: '2.13rem',
            letterSpacing: '0px',
            opacity: 1,
        },
        h3: {
            fontWeight: 'bold',
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
            letterSpacing: '0.1px',
            opacity: 1,
        },
        body1: {
            fontWeight: 'normal',
            fontSize: '1rem',
            lineHeight: '1.38rem',
            letterSpacing: '0.24px',
            opacity: 1,
        },
        body1Emphasis: {
            fontFamily: 'Ubuntu',
            fontWeight: 'bold',
            fontSize: '1rem',
            lineHeight: '1.38rem',
            letterSpacing: '0.24px',
            opacity: 1,
        },
        body2: {
            fontWeight: 'normal',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '0px',
            opacity: 1,
        },
        body2Emphasis: {
            fontFamily: 'Ubuntu',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '0px',
            opacity: 1,
        },
        body3: {
            fontFamily: 'Ubuntu',
            fontWeight: 'normal',
            fontSize: '0.75rem',
            lineHeight: '0.81rem',
            letterSpacing: '0.24px',
            opacity: 1,
        },
        caption: {
            fontWeight: 'normal',
            fontSize: '0.75rem',
            lineHeight: '1rem',
            letterSpacing: '0.24px',
            opacity: 1,
        },
        overline: {
            fontWeight: 'bold',
            fontSize: '0.75rem',
            lineHeight: '1.25rem',
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: palette.text.hint,
            opacity: 1,
        },
        overlineMedium: {
            fontFamily: 'Ubuntu',
            fontWeight: 'normal',
            fontSize: '0.75rem',
            lineHeight: '1.25rem',
            letterSpacing: '0.9px',
            textTransform: 'uppercase',
            color: palette.text.hint,
            opacity: 1,
        },
        overlineActive: {
            fontFamily: 'Ubuntu',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            lineHeight: '1.25rem',
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: palette.text.secondary,
            opacity: 1,
        },
        button: {
            fontWeight: 'bold',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            letterSpacing: '1.75px',
            textTransform: 'capitalize',
            opacity: 1,
        },
    },
});
